// =============================================================================
// MeuDriver — GoogleCalendarConnect
// Botão de conexão/desconexão com o Google Calendar.
// =============================================================================

import { useState } from 'react'
import { CalendarCheck, CalendarX, Loader2, ExternalLink } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface GoogleCalendarStatus {
  connected: boolean
  calendarId: string | null
}

async function fetchCalendarStatus(userId: string): Promise<GoogleCalendarStatus> {
  const { data } = await supabase
    .from('google_calendar_tokens')
    .select('calendar_id')
    .eq('user_id', userId)
    .maybeSingle()

  return { connected: Boolean(data), calendarId: data?.calendar_id ?? null }
}

export function GoogleCalendarConnect() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [isConnecting, setIsConnecting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)

  const { data: status, isLoading } = useQuery<GoogleCalendarStatus, Error>({
    queryKey: ['google_calendar_status', user?.id ?? ''],
    queryFn: () => fetchCalendarStatus(user!.id),
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 5,
  })

  const handleConnect = async () => {
    setIsConnecting(true)
    try {
      const { data, error } = await supabase.functions.invoke<{ url: string }>(
        'google-calendar-auth',
        { method: 'GET' } as Parameters<typeof supabase.functions.invoke>[1],
      )
      if (error || !data?.url) throw new Error(error?.message ?? 'URL OAuth não retornada')
      window.location.href = data.url
    } catch (err) {
      console.error('[GoogleCalendarConnect] connect error:', err)
      toast.error('Erro ao iniciar conexão com o Google Calendar.')
      setIsConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    if (!user?.id) return
    setIsDisconnecting(true)
    try {
      const { error } = await supabase
        .from('google_calendar_tokens')
        .delete()
        .eq('user_id', user.id)

      if (error) throw new Error(error.message)

      toast.success('Google Calendar desconectado.')
      queryClient.invalidateQueries({ queryKey: ['google_calendar_status', user.id] })
    } catch (err) {
      console.error('[GoogleCalendarConnect] disconnect error:', err)
      toast.error('Erro ao desconectar o Google Calendar.')
    } finally {
      setIsDisconnecting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarCheck className="h-4 w-4 text-brand-600" />
          Google Calendar
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="h-10 animate-pulse rounded-md bg-gray-100" />
        ) : status?.connected ? (
          <>
            <p className="text-sm text-muted-foreground">
              Suas aulas confirmadas serão adicionadas automaticamente ao seu Google Calendar.
            </p>
            <div className="flex items-center gap-2 text-sm text-green-700 font-medium">
              <CalendarCheck className="h-4 w-4" />
              Conectado
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full text-destructive border-destructive/30 hover:bg-destructive/5"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              data-testid="gcal-disconnect-btn"
            >
              {isDisconnecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CalendarX className="h-3.5 w-3.5 mr-1.5" />
                  Desconectar
                </>
              )}
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Conecte seu Google Calendar para sincronizar aulas automaticamente.
            </p>
            <Button
              size="sm"
              className="w-full"
              onClick={handleConnect}
              disabled={isConnecting}
              data-testid="gcal-connect-btn"
            >
              {isConnecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                  Conectar Google Calendar
                </>
              )}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}
